import { inject, injectable } from 'inversify';
import i18next from 'i18next';
import { Amplify } from 'aws-amplify';
import { jwtDecode } from 'jwt-decode';
import {
  type AuthSession,
  type AuthUser,
  confirmResetPassword,
  confirmSignIn,
  decodeJWT,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  signIn,
  type SignInOutput,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword,
  updateUserAttribute,
} from '@aws-amplify/auth';
import { DefaultTokenStore } from '@aws-amplify/auth/cognito';
import { defaultStorage } from '@aws-amplify/core';

import type {
  AuthData,
  AuthorizeCode,
  ChangePassword,
  ChangePasswordWithOldPassword,
  CheckCode,
  ConfirmLogin,
  Customer,
  CustomerConsent,
  CustomFormField,
  DeleteAccount,
  ExportAccountData,
  GenerateCode,
  GenerateCodeResponse,
  GetCaptureStatus,
  GetCustomerConsents,
  GetExternalLoginProviders,
  GetFavorites,
  GetPublisherConsents,
  GetUser,
  GetWatchHistory,
  Login,
  LoginAuthenticatedResponse,
  LoginChallenge,
  LoginUnauthenticatedResponse,
  LoginExternal,
  Logout,
  Register,
  ResetPassword,
  SetAuthData,
  UpdateCaptureAnswers,
  UpdateCustomer,
  UpdateCustomerConsents,
  UpdateFavorites,
  UpdateWatchHistory,
} from '../../../../types/account';
import type { AccessModel, Config } from '../../../../types/config';
import AccountService from '../AccountService';
import { ACCESS_MODEL } from '../../../constants';
import env from '../../../env';
import { createURL } from '../../../utils/urlFormatting';
import { logDebug, logError } from '../../../logger';
import StorageService from '../../StorageService';
import { useConfigStore } from '../../../stores/ConfigStore';

const VIEWER_TOKEN_SESSION_KEY = 'OTT_VIEWER_TOKEN';

@injectable()
export default class AWSCognitoAccountService extends AccountService {
  accessModel: AccessModel = ACCESS_MODEL.SVOD;
  svodOfferIds: string[] = [];
  sandbox = false;
  configId: string | undefined;
  stripeProducts: { id: string; label: string }[] | undefined;

  // checkout parameters
  productId: string | undefined;
  invoiceMode: boolean = false;
  coupon: string | undefined;
  promotionCode: string | undefined;

  // viewer token parameters
  viewerToken: { jwt: string; access: string; sub: string; provider: string } | undefined;

  constructor(@inject(StorageService) private readonly storageService: StorageService) {
    super({
      canUpdateEmail: false,
      canSupportEmptyFullName: false,
      canChangePasswordWithOldPassword: true,
      canRenewSubscription: false,
      canExportAccountData: false,
      canUpdatePaymentMethod: false,
      canShowReceipts: false,
      canDeleteAccount: false,
      hasNotifications: false,
      watchListSizeLimit: 40,
    });
  }

  protected formatAccount(user: AuthUser, auth: AuthSession) {
    const email = auth.tokens?.idToken?.payload['email'];
    const firstName = auth.tokens?.idToken?.payload['given_name'];
    const lastName = auth.tokens?.idToken?.payload['family_name'];
    const terms = auth.tokens?.idToken?.payload['custom:acceptedTerms'];

    return {
      id: user.userId,
      uuid: user.userId,
      email: typeof email === 'string' ? email : '',
      fullName: firstName && lastName ? `${firstName} ${lastName}` : '',
      firstName: `${firstName || ''}`,
      lastName: `${lastName || ''}`,
      metadata: {
        terms: terms === 'true',
      },
      regDate: '',
      country: '',
      lastUserIp: '',
    };
  }

  protected getTermsConsent = (): CustomFormField => {
    const termsLink = `<a href="${env.APP_TERMS_AND_CONDITIONS_URL}" target="_blank">${i18next.t('account:registration.terms_and_conditions')}</a>`;

    // t('account:registration.terms_consent')
    return {
      type: 'checkbox',
      isCustomRegisterField: true,
      showInProfile: true,
      required: true,
      name: 'terms',
      defaultValue: '',
      label: i18next.t('account:registration.terms_consent', { termsLink }),
      enabledByDefault: false,
      placeholder: '',
      options: {},
      version: '1',
    };
  };

  protected getNewsletterConsent = ({ siteName }: Config): CustomFormField => {
    // t('account:registration.email_updates')
    // @TODO make this conditional? Now this field is always shown.
    return {
      type: 'checkbox',
      isCustomRegisterField: true,
      showInProfile: false,
      required: false,
      name: 'newsletter',
      defaultValue: '',
      label: i18next.t('account:registration.email_updates', { siteName }),
      enabledByDefault: false,
      placeholder: '',
      options: {},
      version: '1',
    };
  };

  protected exchangeViewerToken = async (token: string) => {
    try {
      const response = await fetch(`${env.APP_ACCOUNT_API_BASE_URL}/exchange-token`, {
        method: 'POST',
        body: JSON.stringify({ credentials: { token } }),
        credentials: 'include',
      });
      const data = (await response.json()) as { message: string } | { token: string };

      return 'token' in data ? data.token : null;
    } catch (error: unknown) {
      logError('AWSCognitoAccountService', 'Failed to exchange viewer token', { error });
    }

    return null;
  };

  protected initializeViewerSession = async (urlToken: string | null) => {
    let token: string | null = null;

    if (urlToken) {
      token = await this.exchangeViewerToken(urlToken);
    } else if (this.storageService) {
      token = await this.storageService.getItem(VIEWER_TOKEN_SESSION_KEY, false);
    }

    if (!token) return;

    try {
      const decoded = jwtDecode<{
        sub: string | undefined;
        access: string | undefined;
        provider: string | undefined;
        exp: number;
      }>(token);

      // expired token
      if (decoded.exp >= Date.now()) {
        logDebug('AWSCognitoAccountService', 'Viewer token expired', { decoded });
        await this.storageService.removeItem(VIEWER_TOKEN_SESSION_KEY, false);
        return;
      }

      if (decoded.sub && decoded.access && decoded.provider) {
        this.viewerToken = {
          jwt: token,
          sub: decoded.sub,
          provider: decoded.provider,
          access: decoded.access,
        };

        if (this.storageService) {
          await this.storageService.setItem(VIEWER_TOKEN_SESSION_KEY, token);
        }

        // switch access model to AVOD so that users with a viewer token gets access to all content
        // single media item access is not supported yet
        this.accessModel = ACCESS_MODEL.AVOD;
      }
    } catch (error: unknown) {
      logError('AWSCognitoAccountService', 'Failed to initialize viewer token', { error });
    }
  };

  protected async getAuthUser(isSignedIn: boolean, nextStep: SignInOutput['nextStep']) {
    let user: Customer | null = null;
    let customerConsents: CustomerConsent[] = [];
    let auth: AuthData | null = null;
    let challenge: LoginChallenge | undefined = undefined;

    if (nextStep) {
      switch (nextStep.signInStep) {
        case 'RESET_PASSWORD':
          challenge = { type: 'NEW_PASSWORD_REQUIRED' };
          break;
        case 'DONE':
          challenge = undefined; // done, no challenge needed
          break;
        default:
          logError('AWSCognitoAccountService', 'Sign in challenge not supported', { step: nextStep });
          throw new Error('Failed to authenticate user.');
      }
    }

    if (isSignedIn) {
      const authUser = await getCurrentUser();
      const authSession = await fetchAuthSession();

      user = this.formatAccount(authUser, authSession);
      customerConsents = await this.getCustomerConsents({ customer: user });
      auth = {
        jwt: authSession.tokens?.idToken?.toString() || '',
        refreshToken: '',
      };
    }

    return {
      isSignedIn,
      challenge,
      auth,
      user,
      customerConsents,
    } as LoginAuthenticatedResponse | LoginUnauthenticatedResponse;
  }

  getViewerToken = () => {
    return this.viewerToken;
  };

  protected parseReferrer = async (referrer: string) => {
    const refererSearchParams = new URLSearchParams(referrer.split('#')[0].split('?')[1]);
    const refererInvoiceMode = refererSearchParams.get('invoice') === 'true';
    const refererToken = refererSearchParams.get('token');
    const product = refererSearchParams.get('product');

    this.promotionCode = refererSearchParams.get('promotionCode') || undefined;
    this.coupon = !this.promotionCode ? refererSearchParams.get('coupon') || undefined : undefined;
    this.invoiceMode = refererInvoiceMode;

    if (product) {
      this.stripeProducts = [{ label: i18next.t('account:choose_offer.subscription'), id: product }];
    }

    // @TODO logout when there is a Cognito session?
    await this.initializeViewerSession(refererToken);
  };

  protected resetReferrerParams = async () => {
    this.coupon = undefined;
    this.promotionCode = undefined;
    this.invoiceMode = false;
  };

  protected getOauthRedirectUrls(): string[] {
    return [env.APP_PUBLIC_URL, 'http://localhost:8080'];
  }

  protected getOauthSignOutRedirectUrl(): string {
    return env.APP_PUBLIC_URL;
  }

  protected getOauthPreferredRedirectUrl(): undefined | string {
    return undefined;
  }

  protected getOauthScopes(): string[] {
    return ['openid', 'profile', 'aws.cognito.signin.user.admin'];
  }

  initialize = async (config: Config, referrer: string, _logoutCallback: () => Promise<void>) => {
    const { stripeProductIds, stripeProductLabels, userPoolId, userPoolClientId, oauthDomain } = config.custom || {};

    this.configId = config.id;

    // Parse stripe products
    // Example:
    // stripe_product_ids: "prod_abc,prod_def"
    // stripe_product_labels: "Monthly,Yearly"
    // --> [{id: "prod_abc", label: "Monthly"}, {id: "prod_def", label: "Yearly"}]
    if (typeof stripeProductIds === 'string') {
      this.stripeProducts = stripeProductIds.split(',').reduce((acc, id, index) => {
        const label = typeof stripeProductLabels === 'string' ? stripeProductLabels.split(',')[index] : index.toString();
        if (label) {
          acc.push({ id, label });
        }
        return acc;
      }, [] as { id: string; label: string }[]);
    }

    // @TODO `svodOfferIds` should be removed, this is only needed for the waiting-for-payments flow (products/offers mismatch now)
    this.svodOfferIds = this.stripeProducts?.map((item) => item.id) || [];

    if (typeof userPoolId !== 'string' || typeof userPoolClientId !== 'string') {
      throw new Error('userPoolId or userPoolClientId not configured in the app config');
    }

    if (typeof oauthDomain !== 'string') {
      throw new Error('oauthDomain is not configured in the app config');
    }

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId,
          userPoolClientId,
          loginWith: {
            email: true,
            oauth: {
              domain: oauthDomain,
              scopes: this.getOauthScopes(),
              redirectSignIn: this.getOauthRedirectUrls(),
              redirectSignOut: this.getOauthRedirectUrls(),
              responseType: 'code',
              // @ts-expect-error untyped param
              preferredRedirectUrl: this.getOauthPreferredRedirectUrl(),
            },
          },
        },
      },
    });

    await this.parseReferrer(referrer);
  };

  login: Login = async ({ email, password }) => {
    let response;

    try {
      response = await signIn({ username: email, password, options: { authFlowType: 'USER_PASSWORD_AUTH' } });

      return this.getAuthUser(response.isSignedIn, response.nextStep);
    } catch {
      throw new Error('Failed to authenticate user.');
    }
  };

  confirmLogin: ConfirmLogin = async ({ answer }) => {
    try {
      const response = await confirmSignIn({ challengeResponse: answer });

      return this.getAuthUser(response.isSignedIn, response.nextStep);
    } catch (error: unknown) {
      logDebug('AWSCognitoAccountService', 'Sign in challenge failed', { error });
      throw error;
    }
  };

  loginExternal: LoginExternal = async ({ provider }) => {
    try {
      await signInWithRedirect({ provider: { custom: provider } });
    } catch (error) {
      logError('AWSCognitoAccountService', 'Failed to sign in with redirect', { error });
      throw new Error('Failed to authenticate user.');
    }
  };

  getAuthData = async (forceRefresh?: boolean) => {
    // Viewer token authentication
    if (this.viewerToken) {
      return {
        jwt: this.viewerToken.jwt,
        refreshToken: '',
      };
    }

    // Cognito authentication
    const auth = await fetchAuthSession({ forceRefresh });

    // External IdP authentication
    const providerName = auth.tokens?.idToken?.payload['custom:providerName'];
    const entitlementStatus = auth.tokens?.idToken?.payload['custom:entitlementStatus'];

    // The user is entitled, but not via Stripe
    if (providerName && entitlementStatus) {
      this.accessModel = ACCESS_MODEL.AVOD;

      // @FIXME this needs to be moved/refactored later
      useConfigStore.setState({ accessModel: this.accessModel });
    }

    if (auth.tokens?.idToken) {
      return {
        jwt: auth.tokens.idToken.toString(),
        refreshToken: '',
      } as AuthData;
    }

    return null;
  };

  getUser: GetUser = async () => {
    if (this.viewerToken) {
      return {
        user: {
          id: this.viewerToken.sub,
          email: '',
          metadata: {},
        },
        customerConsents: [],
      };
    }

    try {
      // Cognito user
      const account = await getCurrentUser();
      const auth = await fetchAuthSession();
      const user = this.formatAccount(account, auth);
      const customerConsents = await this.getCustomerConsents({ customer: user });

      return {
        user,
        customerConsents,
      };
    } catch (error) {
      // failed to get user
      throw new Error('Failed to authenticate user');
    }
  };

  getWatchHistory: GetWatchHistory = async () => {
    if (this.viewerToken) return [];

    try {
      const attributes = await fetchUserAttributes();

      return 'custom:watchHistory' in attributes && typeof attributes['custom:watchHistory'] === 'string' ? JSON.parse(attributes['custom:watchHistory']) : [];
    } catch (error: unknown) {
      logError('AWSCognitoAccountService', 'Failed to restore the watch history', { error });
    }

    return [];
  };

  getFavorites: GetFavorites = async () => {
    if (this.viewerToken) return [];

    try {
      const attributes = await fetchUserAttributes();

      return 'custom:favorites' in attributes && typeof attributes['custom:favorites'] === 'string' ? JSON.parse(attributes['custom:favorites']) : [];
    } catch (error: unknown) {
      logError('AWSCognitoAccountService', 'Failed to restore the favorites', { error });
    }

    return [];
  };

  updateWatchHistory: UpdateWatchHistory = async ({ history }) => {
    if (this.viewerToken) return;

    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'custom:watchHistory',
          value: JSON.stringify(history),
        },
      });
    } catch (error: unknown) {
      logError('AWSCognitoAccountService', 'Failed to store the watch history', { error });
    }
  };

  updateFavorites: UpdateFavorites = async ({ favorites }) => {
    if (this.viewerToken) return;

    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'custom:favorites',
          value: JSON.stringify(favorites),
        },
      });
    } catch (error: unknown) {
      logError('AWSCognitoAccountService', 'Failed to store the favorites', { error });
    }
  };

  updateCustomer: UpdateCustomer = async ({ firstName, lastName }) => {
    try {
      const authSession = await fetchAuthSession();

      await fetch(`${env.APP_ACCOUNT_API_BASE_URL}/update-profile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authSession.tokens?.idToken?.toString()}` },
        body: JSON.stringify({ firstName, lastName }),
      });

      const user = await getCurrentUser();
      const auth = await fetchAuthSession({ forceRefresh: true });

      return this.formatAccount(user, auth);
    } catch (error) {
      throw new Error('Failed to update user');
    }
  };

  register: Register = async ({ email, password, referrer, consents, favorites, watchHistory }) => {
    try {
      const terms = !!consents.find(({ name }) => name === 'terms')?.value;
      const newsletter = !!consents.find(({ name }) => name === 'newsletter')?.value;

      // @TODO sign up can also require a challenge/next step for the user before completing their account
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            locale: i18next.language,
            'custom:acceptedTerms': terms ? 'true' : 'false',
            'custom:acceptedNewsletter': newsletter ? 'true' : 'false',
            'custom:favorites': JSON.stringify(favorites),
            'custom:watchHistory': JSON.stringify(watchHistory),
          },
        },
      });
      return this.login({ email, password, referrer });
    } catch (error: unknown) {
      throw new Error('Failed to create account.');
    }
  };

  logout: Logout = async () => {
    if (this.viewerToken) {
      this.viewerToken = undefined;
      this.accessModel = 'SVOD';
      await this.storageService.removeItem(VIEWER_TOKEN_SESSION_KEY);
    } else {
      await this.resetReferrerParams();
      await signOut({ global: false, oauth: { redirectUrl: this.getOauthSignOutRedirectUrl() } }); // global doesn't help signing out all devices...
    }
  };

  resetPassword: ResetPassword = async ({ customerEmail }) => {
    try {
      await resetPassword({ username: customerEmail });
    } catch (error: unknown) {
      throw new Error('Failed to reset your password.');
    }
  };

  changePasswordWithResetToken: ChangePassword = async ({ resetPasswordToken, newPassword, customerEmail }) => {
    try {
      await confirmResetPassword({ username: customerEmail!, newPassword, confirmationCode: resetPasswordToken });
    } catch (error: unknown) {
      logDebug('AWSCognitoAccountService', 'Failed to change password with token', { error });

      if (error instanceof Error) {
        if (error.message.includes('Invalid code provided') || error.message.includes('Invalid verification code')) {
          throw new Error('Invalid code provided');
        }
      }
      throw new Error('Failed to change your password.');
    }
  };

  changePasswordWithOldPassword: ChangePasswordWithOldPassword = async ({ oldPassword, newPassword }) => {
    try {
      await updatePassword({ oldPassword, newPassword });
    } catch (error: unknown) {
      logDebug('AWSCognitoAccountService', 'Failed to change password with old password', { error });

      if (error instanceof Error && error.message.includes('Incorrect username or password')) {
        throw new Error('old password does not match');
      }
      throw new Error('Failed to change your password.');
    }
  };

  // consents and capture
  getPublisherConsents: GetPublisherConsents = async (config) => {
    if (!env.APP_TERMS_AND_CONDITIONS_URL) {
      return [];
    }

    return [this.getNewsletterConsent(config), this.getTermsConsent()];
  };

  getCustomerConsents: GetCustomerConsents = async ({ customer }) => {
    return [
      {
        name: 'terms',
        needsUpdate: false,
        state: customer.metadata.terms ? 'accepted' : 'declined',
        value: customer.metadata.terms ? 'true' : 'false',
        version: '1',
      },
    ];
  };

  updateCustomerConsents: UpdateCustomerConsents = async () => {
    // not needed until we add more (and optional) consents
    return [];
  };

  getCaptureStatus: GetCaptureStatus = async ({ customer }) => {
    return {
      isCaptureEnabled: true,
      shouldCaptureBeDisplayed: true,
      settings: [
        {
          answer: {
            firstName: customer.firstName || null,
            lastName: customer.lastName || null,
          },
          enabled: true,
          key: 'firstNameLastName',
          required: true,
        },
      ],
    };
  };

  updateCaptureAnswers: UpdateCaptureAnswers = async ({ customer, ...updatedAnswers }) => {
    return this.updateCustomer({ id: customer.id, ...updatedAnswers });
  };

  getCustomerPortalUrl = async ({ redirectUrl }: { redirectUrl: string }) => {
    const token = (await this.getAuthData())?.jwt;

    return createURL(`${env.APP_ACCOUNT_API_BASE_URL}/billing-portal`, {
      token,
      returnUrl: redirectUrl,
    });
  };

  // optional features
  exportAccountData?: ExportAccountData | undefined;
  deleteAccount?: DeleteAccount | undefined;
  getExternalLoginProviders: GetExternalLoginProviders = async ({ redirectUrl }) => {
    if (!env.APP_EXTERNAL_LOGIN_PROVIDERS) {
      return [];
    }

    try {
      const parsed = JSON.parse(env.APP_EXTERNAL_LOGIN_PROVIDERS) as Awaited<ReturnType<GetExternalLoginProviders>>;

      return parsed.map((entry) => ({
        ...entry,
        url: entry.url?.replace('{url}', redirectUrl),
      }));
    } catch (error: unknown) {
      logError('AWSCognitoAccountProvider', 'Failed to parse external login providers', { error });
    }

    return [];
  };
  subscribeToNotifications = async () => false;

  // tv login
  setAuthData: SetAuthData = async ({ authData }) => {
    // this isn't recommended, but works. The refreshToken isn't exposed by Amplify, so we use the token store which
    // Amplify uses under the hood.
    const tokenStore = new DefaultTokenStore();
    tokenStore.setKeyValueStorage(defaultStorage);
    tokenStore.setAuthConfig(Amplify.getConfig().Auth!);

    const accessToken = decodeJWT(authData.jwt);

    await tokenStore.storeTokens({
      accessToken: accessToken,
      refreshToken: authData.refreshToken,
      username: accessToken.payload.sub || '',
      clockDrift: 0,
    });

    // ensure everything is correct by refreshing all tokens
    await fetchAuthSession({ forceRefresh: true });
  };

  generateCode: GenerateCode = async ({ deviceId, deviceName }) => {
    try {
      const response = await fetch(`${env.APP_ACCOUNT_API_BASE_URL}/device-flow/generate`, {
        method: 'POST',
        body: JSON.stringify({ deviceId, deviceName, configId: this.configId }),
      });
      return (await response.json()) as GenerateCodeResponse;
    } catch (error) {
      throw new Error('Failed to generate code');
    }
  };

  authorizeCode: AuthorizeCode = async ({ code }) => {
    if (this.viewerToken) {
      throw new Error('Failed to authorize');
    }
    try {
      // fetch the auth session, and make sure it has the latest state (forced refresh)
      // i.e. session might be revoked by other devices
      const auth = await fetchAuthSession({ forceRefresh: true });

      if (!auth.userSub) {
        throw new Error("Couldn't get auth session");
      }

      // this isn't recommended, but works. The refreshToken isn't exposed by Amplify, so we use the token store which
      // Amplify uses under the hood.
      const tokenStore = new DefaultTokenStore();
      tokenStore.setKeyValueStorage(defaultStorage);
      tokenStore.setAuthConfig(Amplify.getConfig().Auth!);
      const tokens = await tokenStore.loadTokens();

      if (!tokens) {
        logError('AWSCognitoAccountService', "Couldn't get tokens from the Amplify token storage");

        throw new Error("Couldn't get tokens from the Amplify token storage");
      }

      const response = await fetch(`${env.APP_ACCOUNT_API_BASE_URL}/device-flow/authorize`, {
        method: 'POST',
        body: JSON.stringify({ code, refreshToken: tokens.refreshToken }),
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to authorize code: ${response.statusText}`);
      }
    } catch (error) {
      throw new Error('Failed to authorize code');
    }
  };

  checkCode: CheckCode = async ({ code, deviceId }) => {
    try {
      const response = await fetch(createURL(`${env.APP_ACCOUNT_API_BASE_URL}/device-flow/check`, { code, deviceId }));
      const data = (await response.json()) as { tokenType: 'JWT'; idToken: string; accessToken: string; refreshToken: string } | { message: string };

      if ('idToken' in data) {
        const idToken = decodeJWT(data.idToken);

        return {
          customerId: idToken.payload.sub || 'unknown',
          refreshToken: data.refreshToken,
          accessToken: data.accessToken,
          tokenType: data.tokenType,
          expiresIn: idToken.payload.exp || -1,
        };
      }
    } catch (error) {
      throw new Error('Failed to check code');
    }

    throw new Error('Failed to check code');
  };
}
